import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useData } from '../context/DataContext';
import { useAuth } from '../context/AuthContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiX, FiSave, FiEye, FiEyeOff, FiShield, FiMail, FiCheck, FiAlertCircle } = FiIcons;

const ParentForm = ({ parent, onClose, onSave }) => {
  const { addParent, updateParent } = useData();
  const { createUser, sendPasswordReset } = useAuth();
  
  const [showPassword, setShowPassword] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    firstName: parent?.firstName || '',
    lastName: parent?.lastName || '',
    email: parent?.email || '',
    phone: parent?.phone || '',
    address: parent?.address || '',
    password: '',
    createUserAccount: !parent,
  });

  const handleSendReset = async () => {
    if (!parent?.email) return;
    setLoading(true);
    const res = await sendPasswordReset(parent.email);
    setLoading(false);
    if (res.success) setResetSent(true);
    else setError('Failed to send reset link.');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (parent) {
        const updateData = { ...formData };
        delete updateData.password;
        await updateParent(parent.id, updateData);
      } else {
        if (formData.createUserAccount) {
          if (!formData.password) throw new Error('Password is required for portal accounts');
          
          const userResult = await createUser({
            ...formData,
            role: 'parent',
            status: 'active'
          });

          if (!userResult.success) throw new Error(userResult.message);
          
          // Also create the parent record linked to this UID
          await addParent({ ...formData, id: userResult.uid });
        } else {
          await addParent(formData);
        }
      }
      onSave();
    } catch (err) {
      console.error("Form Error:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 backdrop-blur-sm">
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-white rounded-[2.5rem] shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-8 border-b border-gray-100">
          <h2 className="text-2xl font-black text-gray-900 tracking-tight">{parent ? 'Edit Parent' : 'Add New Parent'}</h2>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 transition-all">
            <SafeIcon icon={FiX} className="text-xl" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-8 space-y-8">
          {error && (
            <div className="bg-rose-50 text-rose-700 p-5 rounded-2xl text-sm font-bold border border-rose-100 flex items-center gap-3">
              <SafeIcon icon={FiAlertCircle} className="text-xl flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <input 
              type="text" 
              placeholder="First Name"
              value={formData.firstName} 
              onChange={(e) => setFormData({ ...formData, firstName: e.target.value })} 
              className="w-full p-4 bg-gray-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-indigo-600 outline-none font-bold" 
              required 
            />
            <input 
              type="text" 
              placeholder="Last Name"
              value={formData.lastName} 
              onChange={(e) => setFormData({ ...formData, lastName: e.target.value })} 
              className="w-full p-4 bg-gray-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-indigo-600 outline-none font-bold" 
              required 
            />
            <input 
              type="email" 
              placeholder="Email"
              value={formData.email} 
              onChange={(e) => setFormData({ ...formData, email: e.target.value })} 
              className="w-full p-4 bg-gray-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-indigo-600 outline-none font-bold" 
              required 
            />
            <input 
              type="tel" 
              placeholder="Phone Number"
              value={formData.phone} 
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })} 
              className="w-full p-4 bg-gray-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-indigo-600 outline-none font-bold" 
              required 
            />
          </div>

          {!parent && (
            <div className="bg-gray-50 p-8 rounded-[2.5rem] space-y-6">
              <label className="flex items-center gap-3 cursor-pointer">
                <input 
                  type="checkbox" 
                  checked={formData.createUserAccount} 
                  onChange={(e) => setFormData({ ...formData, createUserAccount: e.target.checked })} 
                  className="w-6 h-6 rounded-lg text-indigo-600" 
                />
                <span className="font-black text-gray-700 uppercase tracking-widest text-xs">Direct Firebase Auth Registration</span>
              </label>

              {formData.createUserAccount && (
                <div className="relative">
                  <SafeIcon icon={FiShield} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input 
                    type={showPassword ? "text" : "password"} 
                    value={formData.password} 
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })} 
                    className="w-full pl-12 pr-12 py-4 bg-white border-2 border-gray-100 rounded-2xl focus:border-indigo-600 outline-none font-bold" 
                    placeholder="Initial Portal Password" 
                    required 
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400">
                    <SafeIcon icon={showPassword ? FiEyeOff : FiEye} />
                  </button>
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end gap-4 pt-8 border-t">
            <button type="button" onClick={onClose} className="px-8 py-4 text-gray-400 font-black text-xs uppercase tracking-widest">Cancel</button>
            <button type="submit" disabled={loading} className="px-10 py-4 bg-indigo-600 text-white rounded-2xl font-black text-xs uppercase tracking-[0.2em] shadow-xl hover:bg-indigo-700 disabled:opacity-50">
              {loading ? 'Creating in Firebase...' : (parent ? 'Update Record' : 'Register Parent')}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default ParentForm;